import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, filter, take } from 'rxjs';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    google: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loaded$ = new BehaviorSubject<boolean>(false);
  private loading$ = new BehaviorSubject<boolean>(false);
  private scriptElement: HTMLScriptElement | null = null;

  constructor() {}

  /**
   * Dynamically loads the Google Maps JavaScript API
   * @returns Observable<boolean> - Emits true when loaded successfully
   */
  loadScript(): Observable<boolean> {
    // Check if Google Maps is already loaded
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
      this.loaded$.next(true);
      return of(true);
    }

    // If already loading, wait for the existing load to complete
    if (this.loading$.value) {
      return this.loaded$.pipe(
        filter(loaded => loaded),
        take(1),
      );
    }

    // Start loading
    this.loading$.next(true);

    return new Observable<boolean>(observer => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.loaded$.next(true);
        this.loading$.next(false);
        this.scriptElement = script;
        observer.next(true);
        observer.complete();
      };

      script.onerror = error => {
        this.loading$.next(false);
        console.error('Failed to load Google Maps API', error);
        observer.error('Failed to load Google Maps API');
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Check if Google Maps API is loaded
   * @returns boolean
   */
  isLoaded(): boolean {
    return this.loaded$.value;
  }

  /**
   * Get the loading state as an observable
   * @returns Observable<boolean>
   */
  getLoadedState(): Observable<boolean> {
    return this.loaded$.asObservable();
  }
}
